import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useContent } from "../lib/content";
import { ShieldCheck, Mail, ArrowLeft } from "lucide-react";

const COLOR = {
  blue:   { border: "#3B82F6", text: "text-[#3B82F6]" },
  purple: { border: "#A855F7", text: "text-[#A855F7]" },
  green:  { border: "#00FF94", text: "text-[#00FF94]" },
};

export default function Compliance() {
  const { lang, tField } = useI18n();
  const { content } = useContent();

  const c = content?.compliance;
  const regions = c?.regions || [];

  const itemsFor = (r) => r[`items_${lang}`] || r.items_en || r.items_ja || [];

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-12 lg:py-20">
      <Link
        to="/"
        data-testid="compliance-back-link"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white mb-10"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
        Back
      </Link>

      <div className="flex items-start gap-4 mb-3">
        <div className="h-10 w-10 border border-[#3B82F6] text-[#3B82F6] flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
            // LEGAL · COMPLIANCE
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-white" style={{ fontWeight: 800 }}>
            {tField(c?.title) || "Legal & Compliance"}
          </h1>
        </div>
      </div>

      <p className="text-sm text-[#94A3B8] leading-relaxed max-w-3xl mb-12">
        {tField(c?.subtitle)}
      </p>

      <div className="grid md:grid-cols-2 gap-5">
        {regions.map((r, idx) => {
          const col = COLOR[r.color] || COLOR.blue;
          return (
            <article
              key={r.code || idx}
              data-testid={`compliance-region-${(r.code || idx).toString().toLowerCase()}`}
              className="bg-[#0F1525] border border-[#1E2A3F] hover:border-[var(--ac)] transition-colors p-6"
              style={{ "--ac": col.border }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${col.text}`}>
                  {r.code} · {r.law}
                </div>
                <div className={`h-2 w-2 ${col.text}`} style={{ background: col.border }} />
              </div>
              <h2 className="font-display text-xl tracking-tight text-white mb-4" style={{ fontWeight: 800 }}>
                {tField(r.title)}
              </h2>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                {itemsFor(r).map((item, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className={`font-mono ${col.text} shrink-0`}>—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>

      {c?.footnote && (
        <div className="mt-12 border-t border-[#1E2A3F] pt-6 flex items-start gap-3 text-sm text-[#94A3B8]">
          <Mail className="h-4 w-4 mt-0.5 text-[#3B82F6] shrink-0" strokeWidth={1.5} />
          <p className="leading-relaxed">{tField(c.footnote)}</p>
        </div>
      )}
    </div>
  );
}
